import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./OrderPages.css";
import useApiBase from "./api/useApiBase";
import AuthGate from "./auth/AuthGate";
import useAuthSession from "./auth/useAuthSession";
import AutoRefreshBar from "./AutoRefreshBar";
import { useSyncedOrderPagesAutoRefresh } from "./orderPagesAutoRefresh";
import usePageTitle from "./usePageTitle";

const formatDateTime = (value) => {
  if (!value) {
    return "N/A";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const pad = (part) => String(part).padStart(2, "0");
  const datePart = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const timePart = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return `${datePart}, ${timePart}`;
};

const formatRelative = (iso, nowMs) => {
  if (!iso) {
    return "—";
  }
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) {
    return "—";
  }
  const sec = Math.max(0, Math.round((nowMs - t) / 1000));
  if (sec < 60) {
    return `${sec} s temu`;
  }
  const min = Math.floor(sec / 60);
  if (min < 60) {
    return `${min} min temu`;
  }
  const hr = Math.floor(min / 60);
  return `${hr} h ${min % 60} min temu`;
};

const parseDateInput = (value) => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
};

const parseNumberInput = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const GA_NORM_THRESHOLD_G = 800;
const RR_NORM_THRESHOLD = 0.005;
const DEFAULT_SELECTED_WINDOW = 10;
const SELECTION_OVERRIDES_KEY = "orderListSelectionOverrides";
const LINE_DATE_RANGES_KEY = "orderListLineDateRanges";
const EXTENDED_VIEW_KEY = "orderListExtendedView";
const AI_STATUS_VIEW_KEY = "orderListAiStatusView";

const pad2 = (part) => String(part).padStart(2, "0");

/** Local ``datetime-local`` bounds for the current calendar day (browser timezone). */
const todayLocalDayBounds = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = pad2(now.getMonth() + 1);
  const d = pad2(now.getDate());
  return {
    start: `${y}-${m}-${d}T00:00`,
    end: `${y}-${m}-${d}T23:59`,
  };
};

const loadOverrides = () => {
  try {
    const raw = window.localStorage.getItem(SELECTION_OVERRIDES_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (err) {
    return {};
  }
};

const saveOverrides = (overrides) => {
  try {
    window.localStorage.setItem(SELECTION_OVERRIDES_KEY, JSON.stringify(overrides));
  } catch (err) {
    return;
  }
};

const loadLineDateRanges = () => {
  try {
    const raw = window.localStorage.getItem(LINE_DATE_RANGES_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (err) {
    return {};
  }
};

const saveLineDateRanges = (ranges) => {
  try {
    window.localStorage.setItem(LINE_DATE_RANGES_KEY, JSON.stringify(ranges));
  } catch (err) {
    return;
  }
};

const loadExtendedView = () => {
  try {
    return window.localStorage.getItem(EXTENDED_VIEW_KEY) === "1";
  } catch (err) {
    return false;
  }
};

const saveExtendedView = (value) => {
  try {
    window.localStorage.setItem(EXTENDED_VIEW_KEY, value ? "1" : "0");
  } catch (err) {
    return;
  }
};

const loadAiStatusView = () => {
  try {
    return window.localStorage.getItem(AI_STATUS_VIEW_KEY) === "1";
  } catch (err) {
    return false;
  }
};

const saveAiStatusView = (value) => {
  try {
    window.localStorage.setItem(AI_STATUS_VIEW_KEY, value ? "1" : "0");
  } catch (err) {
    return;
  }
};

const isLineDateRangeActive = (range) => Boolean(range && (range.from || range.to));

const matchesLineDateRange = (order, range) => {
  if (range.from) {
    if (!order.start_time) {
      return false;
    }
    if (new Date(order.start_time).getTime() < new Date(range.from).getTime()) {
      return false;
    }
  }
  if (range.to) {
    if (!order.end_time) {
      return false;
    }
    if (new Date(order.end_time).getTime() > new Date(range.to).getTime()) {
      return false;
    }
  }
  return true;
};

const computeDefaultSelected = (orders) => {
  const byLine = new Map();
  orders.forEach((order) => {
    if (order.line_no === null || order.line_no === undefined) {
      return;
    }
    if (!byLine.has(order.line_no)) {
      byLine.set(order.line_no, []);
    }
    byLine.get(order.line_no).push(order);
  });

  const selected = new Set();
  byLine.forEach((list) => {
    list
      .slice()
      .sort((a, b) => {
        const aTime = a.start_time ? new Date(a.start_time).getTime() : 0;
        const bTime = b.start_time ? new Date(b.start_time).getTime() : 0;
        return bTime - aTime;
      })
      .slice(0, DEFAULT_SELECTED_WINDOW)
      .forEach((order) => selected.add(String(order.production_order_no)));
  });
  return selected;
};

const formatGiveAway = (value) =>
  value === null || value === undefined ? "N/A" : `${Math.round(Number(value)).toLocaleString()} g`;

const formatRejectionRate = (value) =>
  value === null || value === undefined ? "N/A" : `${(Number(value) * 100).toFixed(2)}%`;

const orderPassesGa = (order) =>
  order.total_ga !== null && order.total_ga !== undefined && Number(order.total_ga) <= GA_NORM_THRESHOLD_G;

const orderPassesRr = (order) =>
  order.rejection_rate !== null && order.rejection_rate !== undefined && Number(order.rejection_rate) <= RR_NORM_THRESHOLD;

const orderGaStayedPositive = (order) => !order.ga_below_zero;

const computeLineSummaries = (orders, isSelected) => {
  const byLine = new Map();
  orders.forEach((order) => {
    if (order.line_no === null || order.line_no === undefined) {
      return;
    }
    if (!byLine.has(order.line_no)) {
      byLine.set(order.line_no, []);
    }
    if (isSelected(order)) {
      byLine.get(order.line_no).push(order);
    }
  });

  return Array.from(byLine.keys())
    .sort((a, b) => Number(a) - Number(b))
    .map((line) => {
      const selected = byLine.get(line);

      let gaOk = 0;
      let rrOk = 0;
      let positiveOk = 0;
      let allOk = 0;
      let gaSum = 0;
      let gaCount = 0;
      let rrSum = 0;
      let rrCount = 0;
      selected.forEach((order) => {
        const gaPass = orderPassesGa(order);
        const rrPass = orderPassesRr(order);
        const positivePass = orderGaStayedPositive(order);
        if (gaPass) gaOk += 1;
        if (rrPass) rrOk += 1;
        if (positivePass) positiveOk += 1;
        if (gaPass && rrPass && positivePass) allOk += 1;
        if (order.total_ga !== null && order.total_ga !== undefined) {
          gaSum += Number(order.total_ga);
          gaCount += 1;
        }
        if (order.rejection_rate !== null && order.rejection_rate !== undefined) {
          rrSum += Number(order.rejection_rate);
          rrCount += 1;
        }
      });

      return {
        line,
        total: selected.length,
        gaOk,
        rrOk,
        positiveOk,
        allOk,
        avgGa: gaCount ? gaSum / gaCount : null,
        avgRr: rrCount ? rrSum / rrCount : null,
      };
    });
};

function TrafficLight({ label, ok, total, suffix, zeroed }) {
  const status = zeroed || total === 0 ? "na" : ok === total ? "green" : "red";
  const count = zeroed ? "0/0" : total === 0 ? "–" : `${ok}/${total}`;
  return (
    <div className="ga-light">
      <span className={`ga-light-dot ga-light-${status}`} />
      <div className="ga-light-text">
        <span className="ga-light-label">
          {label}
          {suffix ? <span className="ga-light-suffix"> {suffix}</span> : null}
        </span>
        <span className="ga-light-count">{count}</span>
      </div>
    </div>
  );
}

const AI_STATUS_RANK = { green: 0, orange: 1, red: 2, grey: 3 };
const AI_MARKER_STATUSES = ["green", "red", "orange", "grey"];
const DEFAULT_AI_MARKER_FILTER = {
  green: true,
  red: true,
  orange: true,
  grey: true,
};

const formatAiIsOnTooltip = (aiIsOn) => {
  if (!aiIsOn || aiIsOn.status !== "orange") {
    return undefined;
  }
  const pctTrue = Number(aiIsOn.pct_true ?? 0);
  const pctFalse = Number(aiIsOn.pct_false ?? 0);
  const pctNull = Number(aiIsOn.pct_null ?? 0);
  return `${pctTrue}% true, ${pctFalse}% false, ${pctNull}% null/not provided`;
};

function AiOnOffMarker({ aiIsOn }) {
  const status = aiIsOn?.status || "grey";
  const title = formatAiIsOnTooltip(aiIsOn);
  return (
    <span
      className={`order-ai-marker order-ai-marker-${status}`}
      title={title}
      aria-label={title || `AI ${status}`}
    />
  );
}

function LastRunInfo({ status }) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const lastRun = status?.last_run;
  const intervalMin = status?.interval_sec ? Math.round(status.interval_sec / 60) : null;
  const intervalSuffix = intervalMin ? ` · co ${intervalMin} min` : "";

  if (!lastRun?.at) {
    return (
      <span className="auto-annotate-info">
        Ostatnio uruchomiono: brak w tej sesji{intervalSuffix}
      </span>
    );
  }

  const when = new Date(lastRun.at);
  const triggerLabel = lastRun.trigger === "manual" ? "ręcznie" : "harmonogram";
  const detail = lastRun.ok === false ? "błąd" : `oznaczono ${lastRun.annotated ?? 0}`;
  return (
    <span
      className="auto-annotate-info"
      title={Number.isNaN(when.getTime()) ? "" : when.toLocaleString()}
    >
      Ostatnio uruchomiono: {formatRelative(lastRun.at, nowMs)}{" "}
      <span className="auto-annotate-meta">({triggerLabel}, {detail})</span>
      {intervalSuffix}
    </span>
  );
}

function OrderListPage() {
  usePageTitle("Orders List");
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderIdFilter, setOrderIdFilter] = useState("");
  const [lineNoFilter, setLineNoFilter] = useState("");
  const [startFrom, setStartFrom] = useState(() => todayLocalDayBounds().start);
  const [endTo, setEndTo] = useState(() => todayLocalDayBounds().end);
  const [nominalMin, setNominalMin] = useState("");
  const [nominalMax, setNominalMax] = useState("");
  const [durationMin, setDurationMin] = useState("");
  const [durationMax, setDurationMax] = useState("");
  const [aiMarkerFilter, setAiMarkerFilter] = useState(() => ({ ...DEFAULT_AI_MARKER_FILTER }));
  const [aiPctMin, setAiPctMin] = useState(0);
  const [skuFilter, setSkuFilter] = useState({});
  const [sortKey, setSortKey] = useState("start_time");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [autoRefresh, setAutoRefresh] = useSyncedOrderPagesAutoRefresh();
  const [selectionOverrides, setSelectionOverrides] = useState(() => loadOverrides());
  const [lineDateRanges, setLineDateRanges] = useState(() => loadLineDateRanges());
  const [extendedView, setExtendedView] = useState(() => loadExtendedView());
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [hasMetrics, setHasMetrics] = useState(false);
  const [aiStatusView, setAiStatusView] = useState(() => loadAiStatusView());
  const [aiStatusLoading, setAiStatusLoading] = useState(false);
  const [hasAiStatus, setHasAiStatus] = useState(false);
  const [configuredLineNos, setConfiguredLineNos] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [savingComment, setSavingComment] = useState(false);
  const [commentError, setCommentError] = useState("");
  const [autoAnnotate, setAutoAnnotate] = useState(null);
  const [autoAnnotateRunning, setAutoAnnotateRunning] = useState(false);
  const [autoAnnotateError, setAutoAnnotateError] = useState("");
  const pageSize = 50;
  const isNavigating = selectedOrder !== null;
  const apiBase = useApiBase();
  const auth = useAuthSession(apiBase);
  const isMountedRef = useRef(true);

  const fetchIntervalMs = useMemo(() => {
    const envInterval = process.env.REACT_APP_FETCH_INTERVAL_MS;
    const parsed = Number(envInterval);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 60000;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const applyTodayFilter = useCallback(() => {
    const bounds = todayLocalDayBounds();
    setStartFrom(bounds.start);
    setEndTo(bounds.end);
  }, []);

  const clearDateFilter = useCallback(() => {
    setStartFrom("");
    setEndTo("");
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      if (extendedView) {
        setMetricsLoading(true);
      }
      if (aiStatusView) {
        setAiStatusLoading(true);
      }
      const params = new URLSearchParams();
      const startBoundary = parseDateInput(startFrom);
      const endBoundary = parseDateInput(endTo);
      if (startBoundary) {
        params.set("start_from", startBoundary.toISOString());
      }
      if (endBoundary) {
        params.set("end_to", endBoundary.toISOString());
      }
      if (!extendedView) {
        params.set("metrics", "0");
      }
      if (aiStatusView) {
        params.set("ai_status", "1");
      }
      params.set("_", Date.now().toString());
      const query = params.toString();
      const url = query ? `/order_id/?${query}` : "/order_id/";
      const response = await auth.fetchWithAuth(url, { cache: "no-store" });
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      const payload = await response.json();
      if (isMountedRef.current) {
        setOrders(Array.isArray(payload?.orders) ? payload.orders : []);
        setHasMetrics(extendedView);
        setHasAiStatus(aiStatusView);
        setError("");
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : "Unknown error");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setMetricsLoading(false);
        setAiStatusLoading(false);
      }
    }
  }, [auth.fetchWithAuth, startFrom, endTo, extendedView, aiStatusView]);

  const fetchAutoAnnotateStatus = useCallback(async () => {
    try {
      const response = await auth.fetchWithAuth("/api/auto_annotate/status", { cache: "no-store" });
      if (!response.ok) {
        return;
      }
      const payload = await response.json();
      if (isMountedRef.current) {
        setAutoAnnotate(payload);
      }
    } catch (err) {
      // status is best-effort; ignore transient errors
    }
  }, [auth.fetchWithAuth]);

  const runAutoAnnotate = useCallback(async () => {
    setAutoAnnotateRunning(true);
    setAutoAnnotateError("");
    try {
      const response = await auth.fetchWithAuth("/api/auto_annotate/run", { method: "POST" });
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }
      const payload = await response.json();
      if (isMountedRef.current) {
        setAutoAnnotate(payload);
      }
      await fetchOrders();
    } catch (err) {
      if (isMountedRef.current) {
        setAutoAnnotateError(err instanceof Error ? err.message : "Unknown error");
      }
    } finally {
      if (isMountedRef.current) {
        setAutoAnnotateRunning(false);
      }
    }
  }, [auth.fetchWithAuth, fetchOrders]);

  useEffect(() => {
    if (!auth.authChecked || !auth.isAuthenticated) {
      return;
    }

    let intervalId = null;
    fetchOrders();
    if (autoRefresh) {
      intervalId = setInterval(fetchOrders, fetchIntervalMs);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [auth.authChecked, auth.isAuthenticated, autoRefresh, fetchIntervalMs, fetchOrders]);

  useEffect(() => {
    if (!auth.authChecked || !auth.isAuthenticated) {
      return;
    }
    let cancelled = false;
    const fetchConfiguredLines = async () => {
      try {
        const response = await auth.fetchWithAuth("/api/config");
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        const payload = await response.json();
        if (cancelled || !isMountedRef.current) {
          return;
        }
        const lineNos = Array.isArray(payload?.line_configs)
          ? payload.line_configs
              .map((line) => Number(line?.line_no))
              .filter((n) => Number.isFinite(n))
          : [];
        setConfiguredLineNos(new Set(lineNos));
      } catch (err) {
        setConfiguredLineNos(null);
      }
    };
    fetchConfiguredLines();
    return () => {
      cancelled = true;
    };
  }, [auth.authChecked, auth.isAuthenticated, auth.fetchWithAuth]);

  useEffect(() => {
    if (!auth.authChecked || !auth.isAuthenticated) {
      return;
    }
    fetchAutoAnnotateStatus();
    const intervalId = setInterval(fetchAutoAnnotateStatus, 30000);
    return () => clearInterval(intervalId);
  }, [auth.authChecked, auth.isAuthenticated, fetchAutoAnnotateStatus]);

  const defaultSelected = useMemo(() => computeDefaultSelected(orders), [orders]);

  const getNaturalSelected = useCallback(
    (order) => {
      if (order.excluded) {
        return false;
      }
      const range = lineDateRanges[order.line_no];
      if (isLineDateRangeActive(range)) {
        return matchesLineDateRange(order, range);
      }
      return defaultSelected.has(String(order.production_order_no));
    },
    [lineDateRanges, defaultSelected],
  );

  const isOrderSelected = useCallback(
    (order) => {
      const key = String(order.production_order_no);
      if (key in selectionOverrides) {
        return selectionOverrides[key];
      }
      return getNaturalSelected(order);
    },
    [selectionOverrides, getNaturalSelected],
  );

  const toggleOrderSelected = useCallback(
    (order) => {
      const key = String(order.production_order_no);
      const natural = getNaturalSelected(order);
      setSelectionOverrides((prev) => {
        const current = key in prev ? prev[key] : natural;
        const nextValue = !current;
        const next = { ...prev };
        if (nextValue === natural) {
          delete next[key];
        } else {
          next[key] = nextValue;
        }
        saveOverrides(next);
        return next;
      });
    },
    [getNaturalSelected],
  );

  const setLineDateRangeField = useCallback((lineNo, field, value) => {
    setLineDateRanges((prev) => {
      const next = {
        ...prev,
        [lineNo]: { ...(prev[lineNo] || { from: "", to: "" }), [field]: value },
      };
      saveLineDateRanges(next);
      return next;
    });
  }, []);

  const resetLineSelection = useCallback(() => {
    setSelectionOverrides({});
    saveOverrides({});
    setLineDateRanges({});
    saveLineDateRanges({});
  }, []);

  const toggleExtendedView = useCallback(() => {
    setExtendedView((prev) => {
      const next = !prev;
      saveExtendedView(next);
      return next;
    });
  }, []);

  const toggleAiStatusView = useCallback(() => {
    setAiStatusView((prev) => {
      const next = !prev;
      saveAiStatusView(next);
      return next;
    });
    setHasAiStatus(false);
    setSortKey((key) => (key === "ai_is_on" ? "start_time" : key));
  }, []);

  const openCommentEditor = useCallback((order) => {
    setEditingComment(String(order.production_order_no));
    setCommentDraft(order.comment || "");
    setCommentError("");
  }, []);

  const cancelCommentEditor = useCallback(() => {
    setEditingComment(null);
    setCommentDraft("");
    setCommentError("");
  }, []);

  const saveComment = useCallback(
    async (order) => {
      const orderId = String(order.production_order_no);
      setSavingComment(true);
      setCommentError("");
      try {
        const response = await auth.fetchWithAuth(`/order_comment/${encodeURIComponent(orderId)}`, {
          method: "POST",
          body: { comment: commentDraft },
        });
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        const payload = await response.json();
        const stored = typeof payload?.comment === "string" ? payload.comment : "";
        setOrders((prev) =>
          prev.map((item) =>
            String(item.production_order_no) === orderId ? { ...item, comment: stored } : item,
          ),
        );
        setEditingComment(null);
        setCommentDraft("");
      } catch (err) {
        setCommentError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setSavingComment(false);
      }
    },
    [auth.fetchWithAuth, commentDraft],
  );

  const lineSummaries = useMemo(() => {
    const summaries = computeLineSummaries(orders, isOrderSelected);
    if (!configuredLineNos) {
      return summaries;
    }
    return summaries.filter((summary) => configuredLineNos.has(Number(summary.line)));
  }, [orders, isOrderSelected, configuredLineNos]);

  const showExtendedColumns = extendedView && hasMetrics;
  const showAiColumn = aiStatusView && hasAiStatus;

  const availableSkus = useMemo(() => {
    const values = new Set();
    orders.forEach((order) => {
      if (!Array.isArray(order.skus)) {
        return;
      }
      order.skus.forEach((sku) => {
        const normalized = String(sku ?? "").trim();
        if (normalized) {
          values.add(normalized);
        }
      });
    });
    return Array.from(values).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );
  }, [orders]);

  const filteredOrders = orders.filter((order) => {
    if (!matchesOrderId(order, orderIdFilter)) {
      return false;
    }

    if (!matchesLineNo(order, lineNoFilter)) {
      return false;
    }

    if (!matchesTimeInterval(order, startFrom, endTo)) {
      return false;
    }

    if (!matchesNominalInterval(order, nominalMin, nominalMax)) {
      return false;
    }

    if (!matchesDurationInterval(order, durationMin, durationMax)) {
      return false;
    }

    if (showAiColumn && !matchesAiMarker(order, aiMarkerFilter)) {
      return false;
    }

    if (showAiColumn && !matchesAiPctMin(order, aiPctMin)) {
      return false;
    }

    if (!matchesSkuFilter(order, availableSkus, skuFilter, showExtendedColumns)) {
      return false;
    }

    return true;
  });

  const sortedOrders = filteredOrders
    .map((order, index) => ({ order, index }))
    .sort((a, b) => {
      const direction = sortDirection === "asc" ? 1 : -1;
      const aValue = getSortValue(a.order, sortKey);
      const bValue = getSortValue(b.order, sortKey);

      if (aValue < bValue) {
        return -1 * direction;
      }
      if (aValue > bValue) {
        return 1 * direction;
      }
      return a.index - b.index;
    })
    .map((item) => item.order);

  const totalPages = Math.max(1, Math.ceil(sortedOrders.length / pageSize));
  const safePage = Math.min(Math.max(currentPage, 1), totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedOrders = sortedOrders.slice(startIndex, endIndex);

  const columnCount = (showExtendedColumns ? 15 : 11) - (showAiColumn ? 0 : 1);

  const rows = [];
  let previousDay = null;

  paginatedOrders.forEach((order) => {
    const currentDay = order?.start_date ?? null;
    if (previousDay && currentDay && currentDay !== previousDay) {
      rows.push({ type: "separator", key: `sep-${currentDay}` });
    }

    rows.push({ type: "order", key: order.production_order_no, order });
    previousDay = currentDay;
  });

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection(key === "production_order_no" ? "asc" : "desc");
  };

  const renderSortLabel = (label, key) => {
    if (sortKey !== key) {
      return label;
    }
    return `${label} ${sortDirection === "asc" ? "\u25B2" : "\u25BC"}`;
  };

  const goToPage = (page) => {
    setCurrentPage(Math.min(Math.max(page, 1), totalPages));
  };

  const toggleAiMarkerFilter = (status) => {
    setAiMarkerFilter((prev) => ({ ...prev, [status]: !prev[status] }));
  };

  const toggleSkuFilter = (sku) => {
    setSkuFilter((prev) => {
      const currentlyOn = prev[sku] !== false;
      return { ...prev, [sku]: !currentlyOn };
    });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [
    orderIdFilter,
    lineNoFilter,
    startFrom,
    endTo,
    nominalMin,
    nominalMax,
    durationMin,
    durationMax,
    aiMarkerFilter,
    aiPctMin,
    skuFilter,
    showExtendedColumns,
    showAiColumn,
    sortKey,
    sortDirection,
  ]);

  return (
    <AuthGate auth={auth}>
      <div className="order-page">
        {lineSummaries.length > 0 && (
          <>
          <div className="ga-summary">
            <div className="ga-summary-title">
              Status linii (zaznaczone ordery — domyślnie ostatnie {DEFAULT_SELECTED_WINDOW})
            </div>
            <div className="ga-summary-grid">
              {lineSummaries.map((summary) => {
                const range = lineDateRanges[summary.line] || {};
                return (
                  <div key={summary.line} className="ga-summary-card">
                    <div className="ga-summary-line">Linia {summary.line}</div>
                    <div className="ga-summary-lights">
                      <TrafficLight
                        label="GA w normie"
                        suffix={`(śr. ${formatGiveAway(summary.avgGa)})`}
                        ok={summary.gaOk}
                        total={summary.total}
                        zeroed={!showExtendedColumns}
                      />
                      <TrafficLight
                        label="RR w normie"
                        suffix={`(śr. ${formatRejectionRate(summary.avgRr)})`}
                        ok={summary.rrOk}
                        total={summary.total}
                        zeroed={!showExtendedColumns}
                      />
                      <TrafficLight label="GA ≥ 0" ok={summary.positiveOk} total={summary.total} zeroed={!showExtendedColumns} />
                      <TrafficLight label="Wszystko OK" ok={summary.allOk} total={summary.total} zeroed={!showExtendedColumns} />
                    </div>
                    <div className="ga-summary-range">
                      <label className="ga-range-field">
                        <span>Od</span>
                        <input
                          type="datetime-local"
                          value={range.from || ""}
                          onChange={(event) => setLineDateRangeField(summary.line, "from", event.target.value)}
                        />
                      </label>
                      <label className="ga-range-field">
                        <span>Do</span>
                        <input
                          type="datetime-local"
                          value={range.to || ""}
                          onChange={(event) => setLineDateRangeField(summary.line, "to", event.target.value)}
                        />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="ga-summary-note">
              Progi: GA ≤ {GA_NORM_THRESHOLD_G} g, RR ≤ {(RR_NORM_THRESHOLD * 100).toFixed(2)}%. Po wybraniu zakresu dat
              uwzględniane są ordery, których start jest po „Od" i koniec przed „Do".
            </div>
          </div>
          <div className="ga-reset-bar">
            <button type="button" className="ga-reset-button" onClick={resetLineSelection}>
              Resetuj wybór
            </button>
          </div>
          </>
        )}
        <div className="order-container">
          <div className="order-header">
            <h1 className="order-title">Orders List</h1>
            <div className="order-header-toggles">
              <label className="order-extended-toggle">
                <input
                  type="checkbox"
                  checked={extendedView}
                  onChange={toggleExtendedView}
                />
                <span>Extended view (GA / RR / status linii)</span>
                {extendedView && metricsLoading && (
                  <span className="order-extended-spinner" aria-label="Ładowanie danych" />
                )}
              </label>
              <label className="order-extended-toggle">
                <input
                  type="checkbox"
                  checked={aiStatusView}
                  onChange={toggleAiStatusView}
                />
                <span>AI on/off (dociągane na żądanie)</span>
                {aiStatusView && aiStatusLoading && (
                  <span className="order-extended-spinner" aria-label="Ładowanie danych AI on/off" />
                )}
              </label>
            </div>
          </div>
          <AutoRefreshBar
            autoRefresh={autoRefresh}
            onAutoRefreshChange={setAutoRefresh}
            fetchIntervalMs={fetchIntervalMs}
            onRefreshNow={fetchOrders}
            subtitleWhenOn={`Lista odświeża się co ${Math.round(fetchIntervalMs / 1000)} s`}
            subtitleWhenOff="Odświeżanie co interwał jest wyłączone"
          />
          <div className="auto-annotate-bar">
            <button
              type="button"
              className="order-btn"
              onClick={runAutoAnnotate}
              disabled={autoAnnotateRunning}
            >
              {autoAnnotateRunning ? "Uruchamianie…" : "Uruchom proces auto walidacji orderów"}
            </button>
            <LastRunInfo status={autoAnnotate} />
            {autoAnnotateError ? (
              <span className="auto-annotate-error">{autoAnnotateError}</span>
            ) : null}
          </div>
          <div className="order-filter-grid">
            <div className="order-filter">
              <label htmlFor="order-id-filter">Order ID</label>
              <input
                id="order-id-filter"
                type="text"
                placeholder="Exact order id"
                value={orderIdFilter}
                onChange={(event) => setOrderIdFilter(event.target.value)}
              />
            </div>
            <div className="order-filter">
              <label htmlFor="order-line-filter">Line no</label>
              <input
                id="order-line-filter"
                type="text"
                inputMode="numeric"
                placeholder="np. 21, 22"
                value={lineNoFilter}
                onChange={(event) => setLineNoFilter(event.target.value)}
              />
            </div>
            <div className="order-filter">
              <label htmlFor="order-start-filter">Start time from</label>
              <input
                id="order-start-filter"
                type="datetime-local"
                value={startFrom}
                onChange={(event) => setStartFrom(event.target.value)}
              />
            </div>
            <div className="order-filter">
              <label htmlFor="order-end-filter">End time to</label>
              <input
                id="order-end-filter"
                type="datetime-local"
                value={endTo}
                onChange={(event) => setEndTo(event.target.value)}
              />
            </div>
            <div className="order-filter order-filter-actions">
              <span className="order-filter-label">Zakres dat</span>
              <div className="order-filter-buttons">
                <button type="button" className="order-filter-btn" onClick={applyTodayFilter}>
                  Dziś
                </button>
                <button type="button" className="order-filter-btn" onClick={clearDateFilter}>
                  Wszystkie
                </button>
              </div>
            </div>
            <div className="order-filter">
              <label htmlFor="order-nominal-min">Nominal weight min</label>
              <input
                id="order-nominal-min"
                type="number"
                inputMode="decimal"
                placeholder="Min"
                value={nominalMin}
                onChange={(event) => setNominalMin(event.target.value)}
              />
            </div>
            <div className="order-filter">
              <label htmlFor="order-nominal-max">Nominal weight max</label>
              <input
                id="order-nominal-max"
                type="number"
                inputMode="decimal"
                placeholder="Max"
                value={nominalMax}
                onChange={(event) => setNominalMax(event.target.value)}
              />
            </div>
            <div className="order-filter">
              <label htmlFor="order-duration-min">Duration min (minutes)</label>
              <input
                id="order-duration-min"
                type="number"
                inputMode="decimal"
                placeholder="Min"
                value={durationMin}
                onChange={(event) => setDurationMin(event.target.value)}
              />
            </div>
            <div className="order-filter">
              <label htmlFor="order-duration-max">Duration max (minutes)</label>
              <input
                id="order-duration-max"
                type="number"
                inputMode="decimal"
                placeholder="Max"
                value={durationMax}
                onChange={(event) => setDurationMax(event.target.value)}
              />
            </div>
            {showAiColumn && (
            <div className="order-filter order-filter-ai-markers">
              <span className="order-filter-label">AI on/off</span>
              <div className="order-ai-marker-filters" role="group" aria-label="AI on/off">
                {AI_MARKER_STATUSES.map((status) => (
                  <label
                    key={status}
                    className="order-ai-marker-filter"
                    title={status}
                    aria-label={status}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(aiMarkerFilter[status])}
                      onChange={() => toggleAiMarkerFilter(status)}
                    />
                    <span
                      className={`order-ai-marker order-ai-marker-${status}`}
                      aria-hidden="true"
                    />
                  </label>
                ))}
              </div>
            </div>
            )}
            {showAiColumn && (
            <div className="order-filter order-filter-ai-pct">
              <label htmlFor="order-ai-pct-min">
                AI on min % <span className="order-ai-pct-value">{aiPctMin}</span>
              </label>
              <input
                id="order-ai-pct-min"
                type="range"
                min={0}
                max={100}
                step={1}
                value={aiPctMin}
                onChange={(event) => setAiPctMin(Number(event.target.value))}
              />
            </div>
            )}
            {showExtendedColumns && availableSkus.length > 0 && (
              <div className="order-filter order-filter-skus">
                <span className="order-filter-label">SKU</span>
                <div className="order-sku-filters" role="group" aria-label="SKU">
                  {availableSkus.map((sku) => (
                    <label key={sku} className="order-sku-filter">
                      <input
                        type="checkbox"
                        checked={skuFilter[sku] !== false}
                        onChange={() => toggleSkuFilter(sku)}
                      />
                      <span className="order-sku-filter-label">{sku}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          {error && <div className="order-error">Error: {error}</div>}
          <div className="order-pagination">
            <span>
              {sortedOrders.length === 0
                ? "0 orders"
                : `Showing ${startIndex + 1}-${Math.min(endIndex, sortedOrders.length)} of ${sortedOrders.length}`}
            </span>
            <div className="order-pagination-controls">
              <button
                type="button"
                onClick={() => goToPage(safePage - 1)}
                disabled={safePage <= 1}
              >
                Prev
              </button>
              <span>
                Page {safePage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => goToPage(safePage + 1)}
                disabled={safePage >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
          {loading || isNavigating ? (
            <div className="order-loader">
              <div className="order-spinner" />
              <p>
                {isNavigating
                  ? `Loading analysis for order: ${selectedOrder}`
                  : "Loading orders..."}
              </p>
            </div>
          ) : (
            <div className="order-table-wrapper">
            <table className="order-table">
              <thead>
                <tr>
                  {showExtendedColumns && (
                    <th className="order-include-col" title="Wlicz do podsumowania linii">✓</th>
                  )}
                  <th>
                    <button
                      type="button"
                      className="order-sort"
                      onClick={() => toggleSort("production_order_no")}
                    >
                      {renderSortLabel("Production Order", "production_order_no")}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className="order-sort"
                      onClick={() => toggleSort("line_no")}
                    >
                      {renderSortLabel("Line No", "line_no")}
                    </button>
                  </th>
                  {showExtendedColumns && (
                    <th>
                      <button
                        type="button"
                        className="order-sort"
                        onClick={() => toggleSort("skus")}
                      >
                        {renderSortLabel("SKU", "skus")}
                      </button>
                    </th>
                  )}
                  <th>
                    <button
                      type="button"
                      className="order-sort"
                      onClick={() => toggleSort("start_time")}
                    >
                      {renderSortLabel("Start Time", "start_time")}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className="order-sort"
                      onClick={() => toggleSort("end_time")}
                    >
                      {renderSortLabel("End Time", "end_time")}
                    </button>
                  </th>
                  <th>
                    <button
                      type="button"
                      className="order-sort"
                      onClick={() => toggleSort("nominal_weight")}
                    >
                      {renderSortLabel("Nominal Weight", "nominal_weight")}
                    </button>
                  </th>
                  {showAiColumn && (
                    <th>
                      <button
                        type="button"
                        className="order-sort"
                        onClick={() => toggleSort("ai_is_on")}
                      >
                        {renderSortLabel("AI on/off", "ai_is_on")}
                      </button>
                    </th>
                  )}
                  <th>
                    <button
                      type="button"
                      className="order-sort"
                      onClick={() => toggleSort("abandon_count")}
                    >
                      {renderSortLabel("Abandon Count", "abandon_count")}
                    </button>
                  </th>
                  {showExtendedColumns && (
                    <th>
                      <button
                        type="button"
                        className="order-sort"
                        onClick={() => toggleSort("total_ga")}
                      >
                        {renderSortLabel("GA", "total_ga")}
                      </button>
                    </th>
                  )}
                  {showExtendedColumns && (
                    <th>
                      <button
                        type="button"
                        className="order-sort"
                        onClick={() => toggleSort("rejection_rate")}
                      >
                        {renderSortLabel("RR", "rejection_rate")}
                      </button>
                    </th>
                  )}
                  <th className="order-comment-col">Comment</th>
                  <th className="order-actions">Logs</th>
                  <th className="order-actions">Export Small</th>
                  <th className="order-actions">Export All</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={columnCount}>No orders found.</td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    if (row.type === "separator") {
                      return (
                        <tr key={row.key} className="order-separator">
                          <td colSpan={columnCount} />
                        </tr>
                      );
                    }

                    const order = row.order;
                    const orderId = order.production_order_no;
                    const selected = isOrderSelected(order);

                    return (
                      <tr key={row.key}>
                        {showExtendedColumns && (
                          <td className="order-include-col">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleOrderSelected(order)}
                              title="Wlicz ten order do podsumowania linii"
                            />
                          </td>
                        )}
                        <td>
                          <a
                            href={`/chart/${encodeURIComponent(orderId)}`}
                            className="order-link"
                            onClick={() => setSelectedOrder(orderId)}
                          >
                            {orderId}
                          </a>
                        </td>
                        <td>{order.line_no ?? "N/A"}</td>
                        {showExtendedColumns && (
                          <td>{formatOrderSkus(order.skus)}</td>
                        )}
                        <td>{formatDateTime(order.start_time)}</td>
                        <td>{formatDateTime(order.end_time)}</td>
                        <td>{order.nominal_weight ?? "N/A"}</td>
                        {showAiColumn && (
                          <td className="order-ai-col">
                            <AiOnOffMarker aiIsOn={order.ai_is_on} />
                          </td>
                        )}
                        <td>{order.abandon_count ?? 0}</td>
                        {showExtendedColumns && (
                          <td className={orderGaStayedPositive(order) ? "" : "order-ga-negative"}>
                            {formatGiveAway(order.total_ga)}
                            {order.ga_below_zero ? <span className="order-ga-flag" title="GA spadł poniżej 0 w trakcie orderu"> ⚠</span> : null}
                          </td>
                        )}
                        {showExtendedColumns && (
                          <td className={orderPassesRr(order) ? "" : "order-rr-high"}>
                            {formatRejectionRate(order.rejection_rate)}
                          </td>
                        )}
                        <td className="order-comment-cell">
                          {editingComment === String(orderId) ? (
                            <div className="order-comment-editor">
                              <textarea
                                className="order-comment-input"
                                value={commentDraft}
                                onChange={(event) => setCommentDraft(event.target.value)}
                                placeholder="Dodaj komentarz…"
                                rows={3}
                                autoFocus
                              />
                              {commentError ? (
                                <div className="order-comment-error">{commentError}</div>
                              ) : null}
                              <div className="order-comment-actions">
                                <button
                                  type="button"
                                  className="order-btn"
                                  onClick={() => saveComment(order)}
                                  disabled={savingComment}
                                >
                                  {savingComment ? "Zapis…" : "Zapisz"}
                                </button>
                                <button
                                  type="button"
                                  className="order-btn"
                                  onClick={cancelCommentEditor}
                                  disabled={savingComment}
                                >
                                  Anuluj
                                </button>
                              </div>
                            </div>
                          ) : order.comment ? (
                            <button
                              type="button"
                              className="order-comment-icon"
                              title={order.comment}
                              onClick={() => openCommentEditor(order)}
                            >
                              💬
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="order-comment-add"
                              onClick={() => openCommentEditor(order)}
                            >
                              + Komentarz
                            </button>
                          )}
                        </td>
                        <td className="order-actions">
                          {order.line_no != null ? (
                            <a
                              href={`/order_logs/${encodeURIComponent(order.line_no)}/${encodeURIComponent(orderId)}`}
                              className="order-btn"
                            >
                              Logs
                            </a>
                          ) : (
                            <span className="order-btn" style={{ opacity: 0.4 }}>
                              Logs
                            </span>
                          )}
                        </td>
                        <td className="order-actions">
                          <a
                            href={`${apiBase}/download_db/${encodeURIComponent(orderId)}/`}
                            className="order-btn"
                          >
                            Small
                          </a>
                        </td>
                        <td className="order-actions">
                          <a
                            href={`${apiBase}/download_full_db/${encodeURIComponent(orderId)}/`}
                            className="order-btn all"
                          >
                            All
                          </a>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </AuthGate>
  );
}

export default OrderListPage;

const formatOrderSkus = (skus) => {
  if (!Array.isArray(skus) || skus.length === 0) {
    return "N/A";
  }
  return skus.join(", ");
};

const getSortValue = (order, key) => {
  if (key === "production_order_no") {
    return String(order.production_order_no ?? "").toLowerCase();
  }
  if (key === "nominal_weight") {
    return Number(order.nominal_weight ?? Number.NEGATIVE_INFINITY);
  }
  if (key === "ai_is_on") {
    return AI_STATUS_RANK[order.ai_is_on?.status] ?? AI_STATUS_RANK.grey;
  }
  if (key === "line_no") {
    return Number(order.line_no ?? Number.NEGATIVE_INFINITY);
  }
  if (key === "skus") {
    return formatOrderSkus(order.skus).toLowerCase();
  }
  if (key === "abandon_count") {
    return Number(order.abandon_count ?? Number.NEGATIVE_INFINITY);
  }
  if (key === "total_ga") {
    return Number(order.total_ga ?? Number.NEGATIVE_INFINITY);
  }
  if (key === "rejection_rate") {
    return Number(order.rejection_rate ?? Number.NEGATIVE_INFINITY);
  }
  if (key === "start_time") {
    const value = order.start_time ? new Date(order.start_time).getTime() : Number.NEGATIVE_INFINITY;
    return Number.isNaN(value) ? Number.NEGATIVE_INFINITY : value;
  }
  if (key === "end_time") {
    const value = order.end_time ? new Date(order.end_time).getTime() : Number.NEGATIVE_INFINITY;
    return Number.isNaN(value) ? Number.NEGATIVE_INFINITY : value;
  }
  return "";
};

const matchesOrderId = (order, orderIdFilter) => {
  const normalized = String(orderIdFilter ?? "").trim().toLowerCase();
  if (!normalized) {
    return true;
  }
  return String(order.production_order_no ?? "").toLowerCase().includes(normalized);
};

const matchesLineNo = (order, lineNoFilter) => {
  const normalized = String(lineNoFilter ?? "").trim();
  if (!normalized) {
    return true;
  }
  const targets = normalized
    .split(/[\s,]+/)
    .map((part) => Number(part))
    .filter((value) => Number.isFinite(value));
  if (targets.length === 0) {
    return false;
  }
  return targets.includes(Number(order.line_no));
};

const matchesTimeInterval = (order, startFrom, endTo) => {
  const startBoundary = parseDateInput(startFrom);
  const endBoundary = parseDateInput(endTo);

  if (!startBoundary && !endBoundary) {
    return true;
  }

  const orderStart = order.start_time ? new Date(order.start_time) : null;
  const orderEnd = order.end_time ? new Date(order.end_time) : null;

  if (!orderStart || !orderEnd) {
    return false;
  }

  if (startBoundary && orderStart < startBoundary) {
    return false;
  }

  if (endBoundary && orderEnd > endBoundary) {
    return false;
  }

  return true;
};

const matchesNominalInterval = (order, nominalMin, nominalMax) => {
  const minValue = parseNumberInput(nominalMin);
  const maxValue = parseNumberInput(nominalMax);

  if (minValue === null && maxValue === null) {
    return true;
  }

  const orderNominal = Number(order.nominal_weight);
  if (!Number.isFinite(orderNominal)) {
    return false;
  }

  if (minValue !== null && orderNominal < minValue) {
    return false;
  }

  if (maxValue !== null && orderNominal > maxValue) {
    return false;
  }

  return true;
};

const matchesDurationInterval = (order, durationMin, durationMax) => {
  const minValue = parseNumberInput(durationMin);
  const maxValue = parseNumberInput(durationMax);

  if (minValue === null && maxValue === null) {
    return true;
  }

  const orderStart = order.start_time ? new Date(order.start_time) : null;
  const orderEnd = order.end_time ? new Date(order.end_time) : null;

  if (!orderStart || !orderEnd) {
    return false;
  }

  const durationMinutes = (orderEnd.getTime() - orderStart.getTime()) / 60000;
  if (!Number.isFinite(durationMinutes)) {
    return false;
  }

  if (minValue !== null && durationMinutes < minValue) {
    return false;
  }

  if (maxValue !== null && durationMinutes > maxValue) {
    return false;
  }

  return true;
};

const matchesAiMarker = (order, markerFilter) => {
  const status = order.ai_is_on?.status || "grey";
  return Boolean(markerFilter?.[status]);
};

const matchesAiPctMin = (order, minPct) => {
  const threshold = Number(minPct);
  if (!Number.isFinite(threshold) || threshold <= 0) {
    return true;
  }
  const pctTrue = Number(order.ai_is_on?.pct_true ?? 0);
  return Number.isFinite(pctTrue) && pctTrue >= threshold;
};

const matchesSkuFilter = (order, availableSkus, skuFilter, active) => {
  if (!active || !availableSkus.length) {
    return true;
  }
  const selected = availableSkus.filter((sku) => skuFilter?.[sku] !== false);
  if (selected.length === availableSkus.length) {
    return true;
  }
  if (selected.length === 0) {
    return false;
  }
  const selectedSet = new Set(selected);
  const orderSkus = Array.isArray(order.skus)
    ? order.skus.map((sku) => String(sku ?? "").trim()).filter(Boolean)
    : [];
  return orderSkus.some((sku) => selectedSet.has(sku));
};
